/**
 * ImageNode - 图片节点（原子节点）
 *
 * 特性：
 * - DecoratorNode（装饰节点）：React 组件渲染
 * - 原子节点：不可分割，整体删除
 * - 存储图片 attachment name
 * - 支持点击预览/删除
 */

import { $applyNodeReplacement, DecoratorNode, type LexicalNode, type NodeKey, type SerializedLexicalNode, Spread } from "lexical";
import { JSX } from "react";
import { NODE_TYPE_ENTITY } from "@/constants/lexical";

export type SerializedImageNode = Spread<
  {
    type: typeof NODE_TYPE_ENTITY.IMAGE;
    version: 1;
    src: string; // attachment name or URL
    altText?: string;
    width?: number;
    height?: number;
    filename?: string; // attachment filename for URL construction
  },
  SerializedLexicalNode
>;

export interface ImageNodeData {
  src: string;
  altText?: string;
  width?: number;
  height?: number;
  filename?: string;
}

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __altText: string | undefined;
  __width: number | undefined;
  __height: number | undefined;
  __filename: string | undefined;

  static getType(): string {
    return NODE_TYPE_ENTITY.IMAGE;
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__width, node.__height, node.__filename, node.__key);
  }

  constructor(src: string, altText?: string, width?: number, height?: number, filename?: string, key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width;
    this.__height = height;
    this.__filename = filename;
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    // 设置节点 key，方便在 DOM 事件中查找对应的 Lexical 节点
    span.dataset.nodeKey = this.__key;
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return (
      <ImageViewer
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        height={this.__height}
        filename={this.__filename}
        key={this.__key}
      />
    );
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const node = $createImageNode(
      serializedNode.src,
      serializedNode.altText,
      serializedNode.width,
      serializedNode.height,
      serializedNode.filename,
    );
    return node;
  }

  exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      type: NODE_TYPE_ENTITY.IMAGE,
      version: 1,
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
      height: this.__height,
      filename: this.__filename,
    };
  }

  getSrc(): string {
    return this.__src;
  }

  setSrc(src: string): void {
    const writable = this.getWritable();
    writable.__src = src;
  }

  getAltText(): string | undefined {
    return this.__altText;
  }

  setAltText(altText?: string): void {
    const writable = this.getWritable();
    writable.__altText = altText;
  }

  getWidth(): number | undefined {
    return this.__width;
  }

  getHeight(): number | undefined {
    return this.__height;
  }

  getFilename(): string | undefined {
    return this.__filename;
  }

  setFilename(filename?: string): void {
    const writable = this.getWritable();
    writable.__filename = filename;
  }

  setWidth(width?: number): void {
    const writable = this.getWritable();
    writable.__width = width;
  }

  setHeight(height?: number): void {
    const writable = this.getWritable();
    writable.__height = height;
  }

  getTextContent(): string {
    return `[图片: ${this.__altText || this.__src}]`;
  }

  isEqualTo(other: LexicalNode): boolean {
    if (!(other instanceof ImageNode)) {
      return false;
    }
    return this.__src === other.__src;
  }
}

/**
 * 创建 ImageNode
 */
export function $createImageNode(src: string, altText?: string, width?: number, height?: number, filename?: string): ImageNode {
  const imageNode = new ImageNode(src, altText, width, height, filename);
  return $applyNodeReplacement(imageNode);
}

/**
 * ImageViewer 组件
 */
function ImageViewer({
  src,
  altText,
  width,
  height,
  filename,
}: {
  src: string;
  altText?: string;
  width?: number;
  height?: number;
  filename?: string;
}) {
  // src 格式：attachments/{uid} (resource name)
  // 需要转换为：/file/attachments/{uid}/{filename}
  let imageUrl = src;

  if (src.startsWith("attachments/")) {
    // 从 resource name 提取 uid
    const uid = src.replace("attachments/", "");
    // 使用 filename 参数或 altText 提供的文件名
    const filepath = filename || altText || "file";
    imageUrl = `/file/attachments/${uid}/${filepath}`;
  }

  return (
    <div className="inline-block my-2">
      <img
        src={imageUrl}
        alt={altText || "image"}
        className="rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
        style={{
          width: width ? `${width}px` : "auto",
          height: height ? `${height}px` : "auto",
          maxWidth: width ? undefined : "100%",
          maxHeight: height ? undefined : "384px",
        }}
      />
    </div>
  );
}
